import { translatePosts } from './translation'

const BLUE_TRACKER_RSS_URL = 'https://www.wowhead.com/blue-tracker?rss'
const WOWHEAD_NEWS_FEEDS = [
  {
    key: 'all',
    label: 'All News',
    shortLabel: 'All',
    url: 'https://www.wowhead.com/news/rss/retail',
    kind: 'aggregate',
    sources: ['retail', 'classic', 'blue'],
  },
  {
    key: 'blue',
    label: 'Blue Tracker',
    shortLabel: 'Blue',
    url: BLUE_TRACKER_RSS_URL,
    kind: 'blue',
  },
  {
    key: 'retail',
    label: 'Retail WoW',
    shortLabel: 'Retail',
    url: 'https://www.wowhead.com/news/rss/retail',
    kind: 'news',
  },
  {
    key: 'classic',
    label: 'Classic WoW',
    shortLabel: 'Classic',
    url: 'https://www.wowhead.com/news/rss/classic',
    kind: 'news',
  }
]
const WOWHEAD_WORKER_URL = 'https://wowhead-rss-proxy.andrey-ilutsa.workers.dev'
const WOWHEAD_ARTICLE_PARSER_VERSION = '2026-05-14-wowhead-markup-v6'
const FEED_CACHE_TTL_MS = 5 * 60 * 1000
const feedCache = new Map()
const feedRequests = new Map()

function createWorkerUrl(path, params = {}) {
  const workerUrl = new URL(path, WOWHEAD_WORKER_URL)
  Object.entries(params).forEach(([key, value]) => {
    if (value) workerUrl.searchParams.set(key, value)
  })
  return workerUrl.toString()
}

function createProxyUrl(url) {
  return createWorkerUrl('/', { url })
}

function decodeHtml(value = '') {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = value
  return textarea.value
}

function stripHtml(value = '') {
  const container = document.createElement('div')
  container.innerHTML = value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|ul|ol|section|article|blockquote|h[1-6])>/gi, ' ')
  return container.textContent || container.innerText || ''
}

function textFromNode(node, selector) {
  return decodeHtml(node.querySelector(selector)?.textContent?.trim() || '')
}

function attrFromNode(node, selector, attr) {
  return node.querySelector(selector)?.getAttribute(attr)?.trim() || ''
}

function normalizeLink(value = '') {
  const link = decodeHtml(value).trim()
  if (!link) return ''
  return link.startsWith('http') ? link : `https://www.wowhead.com${link.startsWith('/') ? '' : '/'}${link}`
}

function getCategory(item) {
  const category = item.querySelector('category')?.textContent?.trim()
  if (category) return decodeHtml(category)

  const title = textFromNode(item, 'title')
  const match = title.match(/\[(.*?)\]/)
  return match?.[1] || 'Blue post'
}

function parseDate(value) {
  const timestamp = Date.parse(value || '')
  return Number.isNaN(timestamp) ? null : new Date(timestamp).toISOString()
}

function findImageUrl(item, rawDescription) {
  const rawContent = textFromNode(item, 'content\\:encoded')
  const candidates = [
    attrFromNode(item, 'media\\:content', 'url'),
    attrFromNode(item, 'media\\:thumbnail', 'url'),
    attrFromNode(item, 'enclosure', 'url'),
    rawContent.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1],
    rawDescription.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1],
    textFromNode(item, 'image url'),
  ].filter(Boolean)

  return normalizeLink(candidates[0] || '')
}

function parseFeed(xmlText, feed = WOWHEAD_NEWS_FEEDS[0]) {
  const documentXml = new DOMParser().parseFromString(xmlText, 'application/xml')
  const parserError = documentXml.querySelector('parsererror')

  if (parserError) {
    throw new Error('Unable to parse Wowhead RSS feed.')
  }

  return [...documentXml.querySelectorAll('item')].map((item, index) => {
    const rawDescription = textFromNode(item, 'description')
    const summary = stripHtml(rawDescription).replace(/\s+/g, ' ').trim()
    const title = textFromNode(item, 'title').replace(/^\[[^\]]+\]\s*/, '')

    return {
      id: textFromNode(item, 'guid') || normalizeLink(textFromNode(item, 'link')) || `${feed.key}-post-${index}`,
      title,
      summary,
      category: getCategory(item),
      feedKey: feed.key,
      feedLabel: feed.label,
      kind: feed.kind,
      imageUrl: findImageUrl(item, rawDescription),
      link: normalizeLink(textFromNode(item, 'link')),
      publishedAt: parseDate(textFromNode(item, 'pubDate')),
    }
  })
}

function uniquePosts(posts) {
  const seen = new Set()
  return posts.filter((post) => {
    const key = post.link || post.id
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function sortPostsByDate(posts) {
  return [...posts].sort((a, b) => {
    return Date.parse(b.publishedAt || '') - Date.parse(a.publishedAt || '')
  })
}

async function requestFeed(url) {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/rss+xml, application/xml, text/xml;q=0.9, */*;q=0.8',
    },
  })

  if (!response.ok) {
    throw new Error(`Wowhead RSS request failed: ${response.status}`)
  }

  return response.text()
}

async function requestJson(url) {
  const response = await fetch(url, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Wowhead worker request failed: ${response.status}`)
  }

  return response.json()
}

export async function getBlueTrackerPosts(lang = 'en') {
  return getWowheadFeedPosts('blue', lang)
}

export async function getWowheadFeedPosts(feedKey = 'blue', lang = 'en') {
  const requestKey = `${feedKey}:${lang}`
  const cached = feedCache.get(requestKey)

  if (cached && Date.now() - cached.timestamp < FEED_CACHE_TTL_MS) {
    return cached.posts
  }

  if (feedRequests.has(requestKey)) {
    return feedRequests.get(requestKey)
  }

  const request = loadWowheadFeedPosts(feedKey, lang)
    .then((posts) => {
      feedCache.set(requestKey, {
        posts,
        timestamp: Date.now(),
      })
      return posts
    })
    .finally(() => {
      feedRequests.delete(requestKey)
    })

  feedRequests.set(requestKey, request)
  return request
}

async function loadWowheadFeedPosts(feedKey = 'blue', lang = 'en') {
  const feed = WOWHEAD_NEWS_FEEDS.find((item) => item.key === feedKey) || WOWHEAD_NEWS_FEEDS[0]
  let posts

  if (feed.kind === 'aggregate') {
    const sourceFeeds = feed.sources
      .map((key) => WOWHEAD_NEWS_FEEDS.find((item) => item.key === key))
      .filter(Boolean)
    const results = await Promise.all(sourceFeeds.map(async (sourceFeed) => parseFeed(await requestFeed(createProxyUrl(sourceFeed.url)), sourceFeed)))
    posts = sortPostsByDate(uniquePosts(results.flat()))
  } else {
    posts = parseFeed(await requestFeed(createProxyUrl(feed.url)), feed)
  }

  return lang === 'en' ? posts : translatePosts(posts, lang)
}

export async function getWowheadArticleImage(url) {
  const article = await getWowheadArticle(url)
  return article.imageUrl || ''
}

export async function getWowheadArticle(url) {
  const normalizedUrl = normalizeLink(url)
  const article = await requestJson(createWorkerUrl('/article', {
    url: normalizedUrl,
    parser: WOWHEAD_ARTICLE_PARSER_VERSION,
  }))

  return {
    title: article.title || 'Wowhead article',
    description: article.description || '',
    imageUrl: article.imageUrl || '',
    author: article.author || '',
    publishedAt: article.publishedAt || '',
    url: article.url || normalizedUrl,
    blocks: Array.isArray(article.blocks) && article.blocks.length
      ? article.blocks
      : [{ type: 'paragraph', text: article.description || 'Open the source article on Wowhead to read the full post.' }],
  }
}

export { BLUE_TRACKER_RSS_URL, WOWHEAD_NEWS_FEEDS }
